import { Link } from "react-router-dom";
import { ArrowRight, Building2, MapPin, MonitorSmartphone } from "lucide-react";
import { SEO } from "@/components/SEO";
import { PageHero } from "@/components/sections/PageHero";
import { ContactForm } from "@/components/sections/ContactForm";
import { RelatedLinksSection } from "@/components/sections/RelatedLinksSection";
import hero from "@/assets/hero-umea.webp";

const Kontakt = () => {
  return (
    <>
      <SEO
        title="Kontakta redovisningsbyrå i Umeå & Stockholm | Cloud"
        description="Kontakta Cloud Redovisning i Umeå eller Stockholm. Vi hjälper företag med bokföring, lön, bokslut, deklaration och rådgivning i hela Sverige."
      />
      <PageHero
        eyebrow="Kontakt"
        title={
          <>
            Kontakta din <span className="italic text-primary">redovisningsbyrå</span>.
          </>
        }
        description="Välj kontoret i Umeå eller Stockholm, eller arbeta med oss helt digitalt. Vi hjälper företag i hela Sverige med bokföring, lön, bokslut, deklaration och rådgivning."
        image={hero}
        imageAlt="Kontakt med Cloud Redovisning i Umeå och Stockholm"
      />

      <section className="py-20 md:py-24">
        <div className="container-page grid gap-6 lg:grid-cols-3">
          <article className="rounded-sm border border-border bg-card p-8">
            <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">
              <Building2 className="h-5 w-5" />
            </div>
            <h2 className="font-display text-3xl text-foreground mt-6">Umeå</h2>
            <p className="mt-4 leading-relaxed text-foreground-muted">
              För företag i Umeå, Vännäs och övriga Västerbotten som vill ha lokal redovisningshjälp
              med personlig kontakt.
            </p>
            <div className="mt-6 space-y-2 text-sm text-foreground/80">
              <p className="inline-flex items-start gap-2">
                <MapPin className="mt-0.5 h-4 w-4 text-primary" />
                Pendelgatan 3b, 904 22 Umeå
              </p>
              <p>Lisa Vilminko, auktoriserad redovisningskonsult</p>
            </div>
            <Link to="/kontakt-umea" className="mt-8 inline-flex items-center gap-2 text-sm font-medium text-primary">
              Till Umeåkontoret <ArrowRight className="h-4 w-4" />
            </Link>
          </article>

          <article className="rounded-sm border border-border bg-card p-8">
            <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">
              <Building2 className="h-5 w-5" />
            </div>
            <h2 className="font-display text-3xl text-foreground mt-6">Stockholm</h2>
            <p className="mt-4 leading-relaxed text-foreground-muted">
              För företag i Stockholm och Mälardalen som söker en modern redovisningsbyrå med lokal
              förankring och digital kapacitet.
            </p>
            <div className="mt-6 space-y-2 text-sm text-foreground/80">
              <p className="inline-flex items-start gap-2">
                <MapPin className="mt-0.5 h-4 w-4 text-primary" />
                Arenavägen 45, plan 16, 121 77 Johanneshov
              </p>
              <p>Linus Sjögren, redovisningskonsult</p>
            </div>
            <Link
              to="/kontakt-stockholm"
              className="mt-8 inline-flex items-center gap-2 text-sm font-medium text-primary"
            >
              Till Stockholmskontoret <ArrowRight className="h-4 w-4" />
            </Link>
          </article>

          <article className="rounded-sm border border-border bg-card p-8">
            <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">
              <MonitorSmartphone className="h-5 w-5" />
            </div>
            <h2 className="font-display text-3xl text-foreground mt-6">Digitalt i hela Sverige</h2>
            <p className="mt-4 leading-relaxed text-foreground-muted">
              Många kunder arbetar helt digitalt med oss i Fortnox. Du får samma personliga stöd
              oavsett om ni finns i Umeå, Stockholm, Vännäs eller någon annan ort.
            </p>
            <div className="mt-6 text-sm text-foreground/80">
              <p>Snabb uppstart, tydliga processer och löpande rådgivning.</p>
            </div>
          </article>
        </div>
      </section>

      <RelatedLinksSection
        title="Vanliga vägar in till ett samarbete."
        description="Börja med den tjänst som ligger närmast ert behov och boka sedan ett möte med rätt kontor."
        links={[
          {
            to: "/bokforing",
            title: "Löpande bokföring",
            body: "Digital bokföring för företag som vill få bättre kontroll och mer tid till verksamheten.",
          },
          {
            to: "/bokslut",
            title: "Bokslut & deklaration",
            body: "Hjälp med årsredovisning, deklaration aktiebolag, K10 och skatteplanering.",
          },
          {
            to: "/loneadministration",
            title: "Löneadministration",
            body: "Lönehantering, AGI, semester, kollektivavtal och trygg backup vid frånvaro.",
          },
          {
            to: "/radgivning",
            title: "Ekonomisk rådgivning",
            body: "Företagsrådgivning för lönsam tillväxt, kassaflöde och bättre beslutsunderlag.",
          },
        ]}
      />

      <ContactForm
        title="Berätta kort om vad ni behöver hjälp med"
        subtitle="Vi återkommer snabbt med rätt upplägg, rätt kontaktperson och nästa steg."
      />
    </>
  );
};

export default Kontakt;
